import React, { useEffect, useState } from 'react';
import { useAppSelector } from '@/store/hooks';
import { router } from '@inertiajs/react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import { 
  TrendingUp, Calendar, RefreshCw, Download, X
} from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";

interface DailyData {
  date: string;
  pnl: number;
  fees: number;
  net_pnl: number;
  win_count: number;
  loss_count: number;
  trade_count: number;
  bots: {
    bot_id: number;
    name: string;
    symbol: string;
    strategy_type: string;
    pnl: number;
    fees: number;
    net_pnl: number;
    trade_count: number;
    win_count: number;
    loss_count: number;
    is_live: boolean;
    use_testnet?: boolean;
  }[];
}

interface StrategyStats {
  strategy: string;
  pnl: number;
  fees: number;
  net_pnl: number;
  win_count: number;
  loss_count: number;
  trade_count: number;
  win_rate: number;
}

interface SignalDailyStatsProps {
  onSelectBot?: (id: string) => void;
}

export const SignalDailyStats: React.FC<SignalDailyStatsProps> = ({ onSelectBot }) => {
  const isPrivate = useAppSelector((state) => state.ui.isPrivate);
  const MODE_KEY = 'signal_daily_stats_mode';
  const MONTH_KEY = 'signal_daily_stats_month';
  const BOT_KEY = 'signal_daily_stats_bot';
  const STRATEGY_KEY = 'signal_daily_stats_strategy';

  const [data, setData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'daily' | 'strategy'>('daily');
  const [strategyStats, setStrategyStats] = useState<StrategyStats[]>([]);
  const [mode, setMode] = useState<'all' | 'mainnet' | 'testnet' | 'paper'>(() => {
    return (localStorage.getItem(MODE_KEY) as any) || 'all';
  });
  const [selectedBot, setSelectedBot] = useState<string>(() => localStorage.getItem(BOT_KEY) || 'all');
  const [strategyFilter, setStrategyFilter] = useState<'all' | 'manual' | 'auto'>(() => {
    return (localStorage.getItem(STRATEGY_KEY) as any) || 'all';
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const saved = localStorage.getItem(MONTH_KEY);
    if (saved) return saved;
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  });

  const [totalStats, setTotalStats] = useState({ pnl: 0, gross_pnl: 0, fees: 0, win_count: 0, total_count: 0 });

  useEffect(() => {
    localStorage.setItem(MODE_KEY, mode);
    localStorage.setItem(MONTH_KEY, selectedMonth);
    localStorage.setItem(BOT_KEY, selectedBot);
    localStorage.setItem(STRATEGY_KEY, strategyFilter);
  }, [mode, selectedMonth, selectedBot, strategyFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/signal/daily_stats?mode=${mode}&month=${selectedMonth}&bot_id=${selectedBot}&strategy_filter=${strategyFilter}`);
      const json = await res.json();
      const dailyData = json.daily_data || [];
      setData(dailyData);
      if (json.total_stats) {
        setTotalStats(json.total_stats);
      }
      setStrategyStats(json.strategy_stats || []);
      if (dailyData.length > 0) {
        setSelectedDate((current) => {
          if (current && dailyData.some((day: DailyData) => day.date === current)) return current;
          return dailyData[dailyData.length - 1].date;
        });
      } else {
        setSelectedDate(null);
      }
    } catch (e) {
      console.error("Failed to fetch signal daily stats", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [mode, selectedMonth, selectedBot, strategyFilter]);

  const winRate = totalStats.total_count > 0 ? ((totalStats.win_count / totalStats.total_count) * 100).toFixed(1) : '0.0';
  const selectedDay = selectedDate ? data.find((day) => day.date === selectedDate) || null : null;

  const [strategyDateFilter, setStrategyDateFilter] = useState<string>('all');

  const displayedStrategyStats = strategyDateFilter === 'all'
    ? strategyStats
    : (() => {
        const day = data.find(d => d.date === strategyDateFilter);
        if (!day) return [];
        const grouped: Record<string, { pnl: number; fees: number; win: number; loss: number; count: number }> = {};
        day.bots.forEach(bot => {
          const key = bot.strategy_type || 'unknown';
          if (!grouped[key]) grouped[key] = { pnl: 0, fees: 0, win: 0, loss: 0, count: 0 };
          grouped[key].pnl += bot.pnl;
          grouped[key].fees += bot.fees;
          grouped[key].count += bot.trade_count;
          grouped[key].win += bot.win_count;
          grouped[key].loss += bot.loss_count;
        });
        return Object.entries(grouped).map(([strategy, g]) => ({
          strategy,
          pnl: g.pnl,
          fees: g.fees,
          net_pnl: g.pnl - g.fees,
          win_count: g.win,
          loss_count: g.loss,
          trade_count: g.count,
          win_rate: g.count > 0 ? +(g.win / g.count * 100).toFixed(1) : 0.0
        })).sort((a, b) => b.net_pnl - a.net_pnl);
      })();

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const d = payload[0].payload as DailyData;
      return (
        <div className="bg-card border border-border p-3 rounded-xl shadow-xl min-w-[180px]">
          <p className="text-xs font-bold text-muted-foreground mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground font-medium">총수익:</span>
              <span className={`font-black ${d.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                {isPrivate ? '****' : `${d.pnl >= 0 ? '+' : ''}${d.pnl.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground font-medium">수수료:</span>
              <span className="font-black text-danger">
                {isPrivate ? '****' : `-${d.fees.toFixed(2)}`}
              </span>
            </div>
            <div className="flex justify-between text-sm border-t border-border/50 pt-1.5 mt-1.5">
              <span className="text-foreground font-bold">수익총계:</span>
              <span className={`font-black ${d.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                {isPrivate ? '****' : `${d.net_pnl >= 0 ? '+' : ''}${d.net_pnl.toFixed(2)} USDT`}
              </span>
            </div>
          </div>
          <div className="mt-2.5 pt-2.5 border-t border-border/50 grid grid-cols-2 gap-2">
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Trades</p>
              <p className="text-xs font-black">{d.trade_count}</p>
            </div>
            <div>
              <p className="text-[10px] text-muted-foreground uppercase font-bold">Win/Loss</p>
              <p className="text-xs font-black text-success">{d.win_count} <span className="text-muted-foreground">/</span> <span className="text-danger">{d.loss_count}</span></p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-card p-6 rounded-3xl border border-border shadow-sm">
        <div>
          <h2 className="text-2xl font-black tracking-tight text-foreground">일별 성과 분석</h2>
          <p className="text-muted-foreground text-sm font-medium">시그널 봇의 일별 PnL 및 승률 통계를 확인합니다.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button variant={viewMode === 'daily' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setViewMode('daily')}>📅 일별 보기</Button>
            <Button variant={viewMode === 'strategy' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setViewMode('strategy')}>📊 전략별 비교</Button>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button variant={strategyFilter === 'all' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('all')}>전체 봇</Button>
            <Button variant={strategyFilter === 'manual' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('manual')}>🛠️ 수동/지표</Button>
            <Button variant={strategyFilter === 'auto' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyFilter('auto')}>🚀 자율 스캐너</Button>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-2xl border border-border">
            <Button variant={mode === 'all' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('all')}>전체 모드</Button>
            <Button variant={mode === 'mainnet' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('mainnet')}>실전</Button>
            <Button variant={mode === 'testnet' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('testnet')}>테스트넷</Button>
            <Button variant={mode === 'paper' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setMode('paper')}>SIM</Button>
          </div>
          <div className="flex items-center gap-2 rounded-2xl border border-border bg-background px-3 h-10">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="bg-transparent text-sm font-bold text-foreground outline-none w-32 cursor-pointer"
            />
          </div>
          <Button variant="outline" size="sm" className="rounded-xl border-border bg-background h-10" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> 갱신
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 총수익</p>
          <p className={`text-lg sm:text-2xl font-black mt-1 ${totalStats.gross_pnl >= 0 ? 'text-success' : 'text-danger'}`}>
             {isPrivate ? '****' : `${totalStats.gross_pnl >= 0 ? '+' : ''}${totalStats.gross_pnl.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 수수료</p>
          <p className="text-lg sm:text-2xl font-black text-danger mt-1">
             {isPrivate ? '****' : `-${totalStats.fees.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card border-primary/20 bg-primary/5">
          <p className="text-[10px] font-black text-primary uppercase tracking-widest">선택 월 수익총계 (Net)</p>
          <p className={`text-lg sm:text-2xl font-black mt-1 ${totalStats.pnl >= 0 ? 'text-success' : 'text-danger'}`}>
             {isPrivate ? '****' : `${totalStats.pnl >= 0 ? '+' : ''}${totalStats.pnl.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50 font-bold">USDT</span>
          </p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">선택 월 총 거래</p>
          <p className="text-lg sm:text-2xl font-black text-foreground mt-1">{totalStats.total_count} <span className="text-[10px] sm:text-xs text-muted-foreground font-bold">회</span></p>
        </Card>
        <Card className="rounded-2xl border border-border shadow-md p-5 bg-card">
          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">평균 승률</p>
          <p className="text-lg sm:text-2xl font-black text-foreground mt-1">{winRate}%</p>
        </Card>
      </div>

      {/* 전략별 비교 뷰 */}
      {viewMode === 'strategy' && (
        <>
          {/* 날짜 필터 */}
          <div className="flex items-center gap-3 bg-card p-4 rounded-2xl border border-border shadow-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold text-muted-foreground">날짜:</span>
            <div className="flex flex-wrap gap-1">
              <Button variant={strategyDateFilter === 'all' ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-3" onClick={() => setStrategyDateFilter('all')}>전체 기간</Button>
              {[...data].reverse().map(day => (
                <Button key={day.date} variant={strategyDateFilter === day.date ? 'default' : 'ghost'} size="sm" className="rounded-xl font-bold text-[10px] h-8 px-2" onClick={() => setStrategyDateFilter(day.date)}>
                  {day.date.slice(5)}
                </Button>
              ))}
            </div>
            {strategyDateFilter !== 'all' && (
              <Button variant="ghost" size="sm" className="rounded-xl text-[10px] h-8 px-2 ml-auto text-muted-foreground" onClick={() => setStrategyDateFilter('all')}>초기화</Button>
            )}
          </div>

          {/* 전략별 요약 카드 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {displayedStrategyStats.slice(0, 4).map((s) => {
              const bestPnl = Math.max(...displayedStrategyStats.map(x => x.net_pnl));
              const isBest = s.net_pnl === bestPnl && bestPnl > 0;
              return (
                <Card key={s.strategy} className={`rounded-2xl border shadow-md p-5 bg-card ${isBest ? 'border-primary/20 bg-primary/5' : 'border-border'}`}>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest truncate">{s.strategy}</p>
                  <p className={`text-lg sm:text-2xl font-black mt-1 ${s.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>
                    {isPrivate ? '****' : `${s.net_pnl >= 0 ? '+' : ''}${s.net_pnl.toFixed(2)}`} <span className="text-[10px] sm:text-xs opacity-50">USDT</span>
                  </p>
                  <div className="flex justify-between text-xs mt-2 font-bold text-muted-foreground">
                    <span>{s.trade_count}회 / {s.win_rate}%</span>
                    <span className={s.win_rate >= 50 ? 'text-success' : 'text-danger'}>{s.win_count}승 {s.loss_count}패</span>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* 전략별 비교 차트 */}
          <Card className="rounded-3xl border border-border shadow-sm p-6 bg-card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-primary/10 rounded-xl text-primary"><TrendingUp className="w-4 h-4" /></div>
                <h3 className="font-black text-lg">전략별 PnL 비교</h3>
              </div>
            </div>
            <div className="h-[300px] w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayedStrategyStats} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                  <XAxis dataKey="strategy" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }} />
                  <Tooltip content={({ active, payload }: any) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload as StrategyStats;
                      return (
                        <div className="bg-card border border-border p-3 rounded-xl shadow-xl min-w-[180px]">
                          <p className="text-xs font-bold text-muted-foreground mb-2">{d.strategy}</p>
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground font-medium">PnL:</span><span className={`font-black ${d.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${d.net_pnl >= 0 ? '+' : ''}${d.net_pnl.toFixed(2)}`}</span></div>
                            <div className="flex justify-between text-xs"><span className="text-muted-foreground font-medium">수수료:</span><span className="font-black text-danger">{isPrivate ? '****' : `-${d.fees.toFixed(2)}`}</span></div>
                            <div className="flex justify-between text-xs border-t border-border/50 pt-1 mt-1"><span className="text-foreground font-bold">거래:</span><span className="font-bold">{d.trade_count}회</span></div>
                            <div className="flex justify-between text-xs"><span className="text-foreground font-bold">승률:</span><span className={`font-black ${d.win_rate >= 50 ? 'text-success' : 'text-danger'}`}>{d.win_rate}%</span></div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
                  <ReferenceLine y={0} stroke="var(--border)" strokeWidth={2} />
                  <Bar dataKey="net_pnl" radius={[4, 4, 0, 0]} barSize={24}>
                    {displayedStrategyStats.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.net_pnl >= 0 ? '#10b981' : '#f43f5e'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 전략별 상세 테이블 */}
          <Card className="rounded-2xl border border-border shadow-sm overflow-hidden bg-card">
            <div className="p-5 border-b border-border bg-muted/20 flex justify-between items-center">
              <h3 className="font-black text-base text-foreground">전략별 상세 통계</h3>
              <span className="text-[10px] font-bold text-muted-foreground">
                {strategyDateFilter === 'all' ? '전체 기간' : strategyDateFilter}
              </span>
            </div>
            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="text-[10px] font-black uppercase">전략</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">거래수</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">승률</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">Win / Loss</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">총수익</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">수수료</TableHead>
                  <TableHead className="text-[10px] font-black uppercase text-right">수익총계</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedStrategyStats.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">데이터가 없습니다.</TableCell></TableRow>
                ) : (
                  displayedStrategyStats.map((s) => (
                    <TableRow key={s.strategy}>
                      <TableCell className="font-black text-xs text-foreground">{s.strategy}</TableCell>
                      <TableCell className="text-right font-bold">{s.trade_count}</TableCell>
                      <TableCell className={`text-right font-black ${s.win_rate >= 50 ? 'text-success' : 'text-danger'}`}>{s.win_rate}%</TableCell>
                      <TableCell className="text-right font-bold"><span className="text-success">{s.win_count}</span> / <span className="text-danger">{s.loss_count}</span></TableCell>
                      <TableCell className={`text-right font-bold ${s.pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${s.pnl >= 0 ? '+' : ''}${s.pnl.toFixed(2)}`}</TableCell>
                      <TableCell className="text-right font-bold text-danger">{isPrivate ? '****' : `-${s.fees.toFixed(2)}`}</TableCell>
                      <TableCell className={`text-right font-black ${s.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${s.net_pnl >= 0 ? '+' : ''}${s.net_pnl.toFixed(2)} USDT`}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {viewMode === 'daily' && (
        <>
      {/* Chart Section */}
      <Card className="rounded-3xl border border-border shadow-sm p-6 bg-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-primary/10 rounded-xl text-primary"><TrendingUp className="w-4 h-4" /></div>
            <h3 className="font-black text-lg">일별 수익총계 변동 (Net PnL)</h3>
          </div>
        </div>
        <div className="h-[300px] w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }} onClick={(state: any) => { if (state?.activeLabel) setSelectedDate(state.activeLabel); }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }} tickFormatter={(val) => val.split('-').slice(1).join('/')} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'var(--muted-foreground)', fontWeight: 600 }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--muted)', opacity: 0.4 }} />
              <ReferenceLine y={0} stroke="var(--border)" strokeWidth={2} />
              <Bar dataKey="net_pnl" radius={[4, 4, 0, 0]} barSize={12}>
                {data.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.net_pnl >= 0 ? '#10b981' : '#f43f5e'} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* 일별 요약 테이블 */}
      <Card className="rounded-2xl border border-border shadow-sm overflow-hidden bg-card">
        <div className="p-5 border-b border-border bg-muted/20"><h3 className="font-black text-base text-foreground">일별 요약 테이블</h3></div>
        <Table>
          <TableHeader className="bg-muted/30">
            <TableRow>
              <TableHead className="text-[10px] font-black uppercase">날짜</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">총수익</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">수수료</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">수익총계</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">Trade</TableHead>
              <TableHead className="text-[10px] font-black uppercase text-right">Win / Loss</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="h-24 text-center text-muted-foreground">데이터가 없습니다.</TableCell></TableRow>
            ) : (
              [...data].reverse().map((day) => (
                <TableRow key={day.date} onClick={() => setSelectedDate(day.date)} className={`cursor-pointer ${selectedDate === day.date ? 'bg-primary/5' : ''}`}>
                  <TableCell className="font-bold text-foreground">{day.date}</TableCell>
                  <TableCell className={`text-right font-bold ${day.pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${day.pnl >= 0 ? '+' : ''}${day.pnl.toFixed(2)}`}</TableCell>
                  <TableCell className="text-right font-bold text-danger">{isPrivate ? '****' : `-${day.fees.toFixed(2)}`}</TableCell>
                  <TableCell className={`text-right font-black ${day.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${day.net_pnl >= 0 ? '+' : ''}${day.net_pnl.toFixed(2)}`}</TableCell>
                  <TableCell className="text-right font-bold text-foreground">{day.trade_count}</TableCell>
                  <TableCell className="text-right font-bold"><span className="text-success">{day.win_count}</span> / <span className="text-danger">{day.loss_count}</span></TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* 선택일 상세 봇별 기여도 */}
      {selectedDay && (
        <Card className="rounded-2xl border border-border shadow-sm overflow-hidden bg-card">
          <div className="p-5 border-b border-border bg-muted/20 flex justify-between items-center">
            <h3 className="font-black text-base text-foreground">선택일 봇별 기여도 ({selectedDay.date})</h3>
          </div>
          <Table>
            <TableHeader className="bg-muted/30">
              <TableRow>
                <TableHead className="text-[10px] font-black uppercase">봇 이름 / 심볼</TableHead>
                <TableHead className="text-[10px] font-black uppercase text-right">거래수</TableHead>
                <TableHead className="text-[10px] font-black uppercase text-right">총수익</TableHead>
                <TableHead className="text-[10px] font-black uppercase text-right">수수료</TableHead>
                <TableHead className="text-[10px] font-black uppercase text-right">수익총계</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {selectedDay.bots.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="h-24 text-center text-muted-foreground">종료된 거래가 없습니다.</TableCell></TableRow>
              ) : (
                [...selectedDay.bots].sort((a, b) => b.net_pnl - a.net_pnl).map((bot) => (
                  <TableRow key={bot.bot_id}>
                    <TableCell>
                      <div 
                        className="font-black text-xs cursor-pointer hover:text-primary transition-colors hover:underline"
                        onClick={() => router.get(`/signal/${bot.bot_id}`)}
                      >
                        #{bot.bot_id} {bot.name}
                      </div>
                      <div className="text-[10px] text-muted-foreground font-bold flex items-center gap-1 mt-0.5">
                        <Badge variant="outline" className="text-[8px] px-1 h-3.5 py-0">{bot.strategy_type}</Badge>
                        {bot.symbol}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <p className="font-bold text-xs">{bot.trade_count}</p>
                      <p className="text-[10px] font-bold text-muted-foreground"><span className="text-success">{bot.win_count}</span> / <span className="text-danger">{bot.loss_count}</span></p>
                    </TableCell>
                    <TableCell className={`text-right font-bold text-xs ${bot.pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${bot.pnl >= 0 ? '+' : ''}${bot.pnl.toFixed(2)}`}</TableCell>
                    <TableCell className="text-right font-bold text-xs text-danger">{isPrivate ? '****' : `-${bot.fees.toFixed(2)}`}</TableCell>
                    <TableCell className={`text-right font-black text-xs ${bot.net_pnl >= 0 ? 'text-success' : 'text-danger'}`}>{isPrivate ? '****' : `${bot.net_pnl >= 0 ? '+' : ''}${bot.net_pnl.toFixed(2)} USDT`}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}
        </>
      )}
    </div>
  );
};
